import SpaceBetween from "@awsui/components-react/space-between";
import React, {useState} from "react";
import NavigationBar from "components/NavigationBar";
import Container from "@awsui/components-react/container";
import Header from "@awsui/components-react/header";
import {FlashbarProps} from "@awsui/components-react/flashbar/interfaces";
import FormField from "@awsui/components-react/form-field";
import Input from "@awsui/components-react/input";
import Select, {SelectProps} from "@awsui/components-react/select";
import Box from "@awsui/components-react/box";
import Link from "@awsui/components-react/link";
import SubmitFlashbar from "components/SubmitFlashbar";
import RiderSynonym from "model/RiderSynonym";
import SearchRiderSynonymsButton from "pages/admin/pages/add_rider_synonym/components/SearchRiderSynonymsButton";
import AddRiderSynonymsButton from "pages/admin/pages/add_rider_synonyms/components/AddRiderSynonymsButton";


const AddRiderSynonymPage: React.FC = () => {

    const [riderName, setRiderName] = useState("");
    const [searchButtonEnabled, setSearchButtonEnabled] = useState(true);
    const [candidates, setCandidates] = useState<RiderSynonym[]>();
    const [selectedCandidate, setSelectedCandidate] = useState<RiderSynonym>();
    const [submitMessage, setSubmitMessage] = useState<FlashbarProps.MessageDefinition>();

    const changeRiderName = (newName: string): void => {
        setRiderName(newName);
        setSearchButtonEnabled(true);
        // The candidate list belongs to the name it was searched for. Editing the
        // name after a search would otherwise let the admin submit the previous
        // rider's preferredNameId under the new spelling.
        setCandidates(undefined);
        setSelectedCandidate(undefined);
        // Same reasoning for the flashbar: "«T. Pogacar» already resolves" names a
        // specific spelling, so it must not outlive the spelling it describes.
        setSubmitMessage(undefined);
    }

    const changeSelectedCandidate = (preferredNameId: string | undefined): void => {
        // Match on preferredNameId, never on the synonym text: the candidate list
        // can hold the same display string under two different preferred names
        // (e.g. "Tadej Pogačar" and "Tadej Pogacar"), and picking the wrong one
        // would attach the synonym to the wrong rider.
        setSelectedCandidate(
            candidates?.find(candidate => String(candidate.preferredNameId) === preferredNameId)
        );
    }

    const candidateOptions: SelectProps.Option[] = (candidates || []).map((candidate, index) => ({
        label: `${index} - ${candidate.synonym} (${candidate.distance})`,
        value: String(candidate.preferredNameId)
    }));

    // Picked out of the very list the dropdown renders, so the collapsed trigger
    // shows the same text as the option that was chosen.
    const selectedOption = selectedCandidate
        ? candidateOptions.find(option => option.value === String(selectedCandidate.preferredNameId)) || null
        : null;

    return (
        <SpaceBetween size="l">
            <NavigationBar/>
            <Container
                header={
                    <Box variant={"span"}>
                        <Header variant="h1">Add rider synonym</Header>
                        <Link variant={"info"} href={"/admin"}>Back</Link>
                    </Box>
                }
            >
                <SpaceBetween size={"m"}>
                    {submitMessage && (
                        <SubmitFlashbar
                            submitMessage={submitMessage}
                            setSubmitMessage={setSubmitMessage}
                        />
                    )}
                    <Container header={<Header variant="h3">Unknown spelling</Header>}>
                        <SpaceBetween size={"m"}>
                            <FormField
                                label={"Rider name as it appears in the source"}
                                description={"The spelling that failed to resolve, e.g. \"T. Pogacar\"."}
                            >
                                <Input
                                    inputMode="text"
                                    onChange={({detail}) => changeRiderName(detail.value)}
                                    value={riderName}
                                    disableBrowserAutocorrect
                                />
                            </FormField>
                            <Box float="right">
                                <SearchRiderSynonymsButton
                                    riderName={riderName}
                                    buttonEnabled={searchButtonEnabled}
                                    setButtonEnabled={setSearchButtonEnabled}
                                    setSubmitMessage={setSubmitMessage}
                                    setCandidates={setCandidates}
                                />
                            </Box>
                        </SpaceBetween>
                    </Container>

                    {(candidates != undefined && candidates.length > 0) ? (
                        <Container header={<Header variant="h3">Matching riders</Header>}>
                            <SpaceBetween size={"m"}>
                                <FormField label={`Rider that "${riderName.trim()}" should resolve to`}>
                                    <Select
                                        selectedOption={selectedOption}
                                        onChange={({detail}) =>
                                            changeSelectedCandidate(detail.selectedOption.value)}
                                        options={candidateOptions}
                                        filteringType="auto"
                                        placeholder="Select a rider"
                                        selectedAriaLabel="Selected"
                                    />
                                </FormField>
                                {/* Already right-aligned: AddRiderSynonymsButton wraps itself in a
                                    floated Box, so it needs no wrapper here. */}
                                <AddRiderSynonymsButton
                                    synonyms={
                                        selectedCandidate
                                            ? new Map([[riderName.trim(), selectedCandidate]])
                                            : undefined
                                    }
                                    setSubmitMessage={setSubmitMessage}
                                />
                            </SpaceBetween>
                        </Container>
                    ) : null}
                </SpaceBetween>
            </Container>
        </SpaceBetween>
    )
};

export default AddRiderSynonymPage;
